// GL16 GPC 배송비 분담금 단가 관리 — MASS 계약 타입.
// BE: mass CostSharePriceController (@RequestMapping("/v1/cost-share/prices"), 게이트웨이 경로 /api2/mass)
//   GET  /matrix — 카테고리 × 국가 그룹 단가 매트릭스(행 = 물류카테고리, 열 = 국가 그룹)
//   POST /       — 셀 1건 단가 저장(항상 새 행 추가, 걸치는 기존 행은 시작일 전날로 마감)
//   GET  /excel-template · /excel-download — 양식·조회 결과 xlsx(카테고리 1행 × 국가 그룹 15열)
//   POST /excel-validation · /excel-upload — 엑셀 업로드 검증 미리보기 · 확정 저장(multipart, 파일을 매번 다시 보낸다)
//   GET  /histories — 단가 변경 이력(최신순, size 최대 100)

/** 단가 축 국가 그룹 — BE 선언 순서. 열도 이 순서다. KR 은 없다(주면 400). */
export const GPC_RATE_COUNTRY_GROUP_CODES = ['JP', 'CN', 'US', 'HK', 'TW', 'SG', 'AU', 'CA', 'NZ', 'TH', 'MY', 'PH', 'VN', 'ID', 'ETC'] as const

export type GpcRateCountryGroupCode = (typeof GPC_RATE_COUNTRY_GROUP_CODES)[number]

/** 국가 그룹 표시명 — 조회 응답(countryGroups[].name)이 정본이고, 이 상수는 응답 전(필터 옵션)에만 쓴다. */
export const GPC_RATE_COUNTRY_GROUP_NAMES: Record<GpcRateCountryGroupCode, string> = {
  JP: '일본',
  CN: '중국',
  US: '미국',
  HK: '홍콩',
  TW: '대만',
  SG: '싱가포르',
  AU: '호주',
  CA: '캐나다',
  NZ: '뉴질랜드',
  TH: '태국',
  MY: '말레이시아',
  PH: '필리핀',
  VN: '베트남',
  ID: '인도네시아',
  ETC: '그 외 전체',
}

export interface GpcRateCountryGroup {
  code: GpcRateCountryGroupCode
  name: string
}

/** 현행 단가의 종료일 — BE 가 무기한을 이 값으로 표현한다. */
export const GPC_RATE_OPEN_END_DATE = '9999-12-31'

/** 단가 상한(미포함) — BE 가 이 값 이상의 단가 저장을 거절한다. */
export const GPC_RATE_UNIT_PRICE_LIMIT = 100000

/** 매트릭스 size 상한 — BE maximum 1000. */
export const GPC_RATE_MATRIX_MAX_SIZE = 1000

/** 저장 409 의 meta.errorCode — 화면이 본 셀 상태와 서버 상태가 다름(낙관적 잠금). */
export const GPC_RATE_CONFLICT_ERROR_CODE = 'CONFLICT'

/** 카테고리 단가 한 행. id·version 은 저장 시 낙관적 잠금 판정에 되돌려 보낸다. */
export interface GpcRatePrice {
  id: number
  version: number | null // 컬럼은 NOT NULL 이라 실데이터에서는 항상 값이 있다
  unitPrice: number // PCS 1개당 단가(원)
  startDate: string // yyyy-MM-dd
  endDate: string // yyyy-MM-dd, 현행은 9999-12-31
}

/** 매트릭스 셀 — 카테고리 × 국가 그룹. */
export interface GpcRateMatrixCell {
  countryGroup: GpcRateCountryGroup
  currentPrice: GpcRatePrice | null // 기준일에 유효한 단가. 없으면 미등록 셀
  reservedPrices: GpcRatePrice[] // 기준일 이후 시작하는 예약 단가, 시작일 오름차순
}

/** 매트릭스 행 — 물류카테고리 1개. cells 는 countryGroups 와 같은 순서. */
export interface GpcRateMatrixRow {
  logisticsCategoryCode: string
  categoryDepth1: string
  categoryDepth2: string | null
  categoryDepth3: string | null
  lastUpdatedAt: string | null // date-time
  cells: GpcRateMatrixCell[]
}

/** 조회 조건 — 국가 그룹 필터는 열만 좁히고 행 집합은 바꾸지 않는다. */
export interface GpcRateMatrixFilter {
  basisDate: string // 필수 yyyy-MM-dd
  countryGroups?: GpcRateCountryGroupCode[]
  logisticsCategoryCodes?: string[]
}

export type GpcRateMatrixReq = GpcRateMatrixFilter & {
  page: number // 0부터
  size: number // 1~1000
}

export interface GpcRateMatrixRes {
  basisDate: string
  countryGroups: GpcRateCountryGroup[] // 행이 0건이어도 내려온다
  items: GpcRateMatrixRow[]
  pageInfo: PageInfoType
}

/** 셀 단건 저장 요청. basePriceId·basePriceVersion 은 새 시작일에 걸치는 기존 행의 값 — 없으면 둘 다 생략. */
export interface GpcRateSaveReq {
  logisticsCategoryCode: string
  countryGroup: GpcRateCountryGroupCode
  unitPrice: number // 0 이상 100,000 미만 정수(원)
  startDate: string // 오늘(KST) 포함 미래
  basePriceId?: number
  basePriceVersion?: number
}

export interface GpcRateSavedPrice {
  id: number
  version: number | null
  logisticsCategoryCode: string
  countryGroup: GpcRateCountryGroupCode
  unitPrice: number
  startDate: string
  endDate: string
}

export interface GpcRateClosedPrice {
  id: number
  version: number | null
  startDate: string
  endDate: string // 새 시작일 전날
}

export interface GpcRateSaveRes {
  savedPrice: GpcRateSavedPrice
  closedPrice: GpcRateClosedPrice | null // 걸치는 행이 없었으면 null
}

/** 저장·엑셀 업로드 403 — MOMS 게이트웨이 사용자가 아니면(조회 전용) MASS SecurityConfig 가 막는다. */
export const GPC_RATE_FORBIDDEN_STATUS = 403

/** 엑셀 업로드 실패 meta.errorCode — 파일 단위 400 과 검증 오류 409(data = 그 시점의 검증 결과). */
export const GPC_RATE_EXCEL_ERROR_CODES = {
  invalidFile: 'INVALID_EXCEL_FILE',
  rowCountLimitExceeded: 'EXCEL_ROW_COUNT_LIMIT_EXCEEDED',
  partialValidationFailure: 'EXCEL_PARTIAL_VALIDATION_FAILURE',
  totalValidationFailure: 'EXCEL_TOTAL_VALIDATION_FAILURE',
} as const

/** 엑셀 검증 건수 — 셀(카테고리 1행 × 국가 열) 단위. unregistered 는 unchanged 중 빈 칸이면서 기준일 단가도 없는 셀(저장 뒤에도 미등록). */
export interface GpcRateExcelSummary {
  changed: number // 바뀔 셀(정상)
  unchanged: number // 빈 칸이거나 기준일 단가와 같은 셀
  unregistered: number
  error: number // 1건 이상이면 확정 저장이 거절된다
  zeroPrice?: number // 0원으로 바뀌는 셀 수 (구 응답에 없을 수 있음)
}

/** 바뀔 셀 — 현재(기준일) 단가 → 변경 단가, 적용 기간은 엑셀 행의 시작일·종료일. 미등록 셀이면 currentUnitPrice = null. */
export interface GpcRateExcelChange {
  rowNo: number // 엑셀 행 번호
  logisticsCategoryCode: string
  categoryDepth1: string
  categoryDepth2: string | null
  categoryDepth3: string | null
  countryGroup: GpcRateCountryGroup
  currentUnitPrice: number | null
  unitPrice: number
  startDate: string // yyyy-MM-dd
  endDate: string // yyyy-MM-dd, 현행은 9999-12-31
}

/** 오류 — 행·헤더 단위 오류는 카테고리·국가가 없을 수 있다. message 가 사용자 문구(PRD 검증 문구). */
export interface GpcRateExcelError {
  rowNo: number
  logisticsCategoryCode: string | null
  countryGroup: GpcRateCountryGroup | null
  value: string | null // 셀에 적힌 원문
  message: string
}

/** 엑셀 검증 결과 — 검증 미리보기 응답이자, 확정 저장이 오류로 거절될 때(409) 실패 응답의 data. */
export interface GpcRateExcelValidation {
  basisDate: string
  countryGroups: GpcRateCountryGroup[]
  summary: GpcRateExcelSummary
  changes: GpcRateExcelChange[]
  errors: GpcRateExcelError[]
}

/** 엑셀 확정 저장 결과(201). 적용 시작일은 엑셀 행마다 다르다. */
export interface GpcRateExcelUploadRes {
  startDate: string // 가장 이른 적용 시작일
  startDates?: string[] // 중복 없는 적용 시작일, 오름차순 (구 응답에 없을 수 있음)
  savedCount: number // 새로 추가한 단가 행 수(= 바뀐 셀 수)
  closedCount: number // 새 시작일 전날로 마감한 기존 행 수
  unchangedCount: number
}

/** 변경 이력 size 상한 — BE maximum 100. */
export const GPC_RATE_HISTORY_MAX_SIZE = 100

/** 변경 이력 조회 조건 — 둘 다 미지정이면 전체. */
export interface GpcRateHistoryFilter {
  countryGroups?: GpcRateCountryGroupCode[]
  logisticsCategoryCodes?: string[]
}

export type GpcRateHistoryReq = GpcRateHistoryFilter & {
  page: number // 0부터
  size: number // 1~100
}

export interface GpcRateCodeName<C extends string = string> {
  code: C
  name: string
}

/** 변경 이력 한 행 — 단가 행 하나가 변경 한 건. 변경일시·변경자 = 행 등록일시·등록자, 마감 같은 사후 수정은 updatedAt·updatedBy. */
export interface GpcRateHistoryItem {
  id: number
  createdAt: string | null // date-time
  createdBy: string | null
  logisticsCategoryCode: string
  categoryDepth1: string
  categoryDepth2: string | null
  categoryDepth3: string | null
  countryGroup: GpcRateCountryGroup
  previousUnitPrice: number | null // 신규면 null
  unitPrice: number
  startDate: string
  endDate: string
  changeType: GpcRateCodeName<'NEW' | 'REVISION'>
  updatedBy: string | null
  updatedAt: string | null // date-time
}

export type GpcRateHistoryRes = ApiPageResponse<GpcRateHistoryItem>
